import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { IViewerHelper } from '../angularjs';
import { PacsStudyDataVnext } from '../common';
import {
    ExaminationStatusForImaging,
    ModelKeyImage,
    ModelShowImage,
    ModelUSMeasureShow,
    ModelUSStressEchoAssociateImages,
    OpenImagesOptions,
} from '../ebit-viewer-api';
import { DeepPartial } from '../../../utils/types';
import {
    ShowStudyParams,
    ShowStudyWithLastPresentationParams,
    ShowStudyWithPidParams,
    VueClientPhilipsApiService,
} from './vue-client-philips-api.service';
import { Logger } from '@se/se.platform.base';
import { RadioSettingsService } from '@se/platform';

@Injectable({
    providedIn: 'root',
})
export class VueClientPhilipsViewerHelper implements IViewerHelper {
    readonly isVNext = true;
    readonly viewerType = 'VueClientPhilips';

    private initialized = false;

    private vueClientPhilipsApi = inject(VueClientPhilipsApiService);
    private radioSettingsService = inject(RadioSettingsService);
    private logger = inject(Logger);

    constructor() {
        console.log('VueClientPhilipsViewerHelper', { vueClientPhilipsApi: this.vueClientPhilipsApi });
    }

    /**
     * Initialize the VueClientPhilips viewer helper
     */
    async init(): Promise<void> {
        if (this.initialized) {
            return;
        }

        const settings = await firstValueFrom(this.radioSettingsService.radioSettingsAsync);
        const viewerSettings = settings.viewerSettings?.viewerVueClientSettings;
        if (!viewerSettings) {
            return;
        }
        this.vueClientPhilipsApi.init(viewerSettings);

        try {
            // Connect to VueClientPhilips ws
            await firstValueFrom(this.vueClientPhilipsApi.connect());
            this.initialized = true;
            this.logger.info('VueClientPhilips: Viewer helper initialized');
        } catch (error: unknown) {
            this.logger.error(
                'VueClientPhilips: Failed to initialize viewer helper',
                error instanceof Error ? error.message : String(error),
            );
            throw error;
        }
    }

    /**
     * Check if the viewer is open and ready
     */
    async isViewerOpen(): Promise<boolean> {
        return this.vueClientPhilipsApi.isConnected() && this.vueClientPhilipsApi.isLoggedIn();
    }

    async areImagesOpen(): Promise<boolean> {
        return true;
    }

    /**
     * Open the VueClientPhilips viewer
     */
    async openViewer(): Promise<void> {
        if (!this.initialized) {
            await this.init();
        }

        if (!this.vueClientPhilipsApi.isConnected()) {
            await firstValueFrom(this.vueClientPhilipsApi.connect());
        }

        this.logger.info('VueClientPhilips: Viewer connection established');
    }

    /**
     * Close the VueClientPhilips viewer
     */
    async closeViewer(): Promise<void> {
        try {
            if (this.vueClientPhilipsApi.isLoggedIn()) {
                await firstValueFrom(this.vueClientPhilipsApi.logout());
            }

            await firstValueFrom(this.vueClientPhilipsApi.closeSession());
            this.vueClientPhilipsApi.disconnect();
            this.logger.info('VueClientPhilips: Viewer closed');
        } catch (error: unknown) {
            this.logger.error(
                'VueClientPhilips: Error closing viewer',
                error instanceof Error ? error.message : String(error),
            );
            throw error;
        }
    }

    /**
     * Open images in the VueClientPhilips viewer
     */
    async openImages(imagingData: PacsStudyDataVnext[], options: DeepPartial<OpenImagesOptions>): Promise<void> {
        try {
            if (!this.initialized) {
                await this.init();
            }

            if (!this.vueClientPhilipsApi.isConnected()) {
                await firstValueFrom(this.vueClientPhilipsApi.connect());
            }

            if (!this.vueClientPhilipsApi.isLoggedIn()) {
                this.vueClientPhilipsApi.login();
            }
        } catch (error: unknown) {
            this.logger.error(
                'VueClientPhilips: Error opening images',
                error instanceof Error ? error.message : String(error),
            );
            throw new Error('VueClientPhilips: login/connection error. please verify your connection and credentials.');
        }

        try {
            // Handle discard option
            if (options.discardOption?.action === 'Discard') {
                await this.discardImages(options.discardOption.ask);
            }

            // Open each study
            for (const studyData of imagingData) {
                const params: ShowStudyWithPidParams = {
                    pid: studyData.patient.patientId || '',
                    issuer: '',
                    accessionNumber: studyData.study.accessionNumber || '',
                };

                if (options.discardOption?.action === 'Append') {
                    // Use addImages for appending
                    const accessionNumber = studyData.study.accessionNumber || '';
                    if (accessionNumber) {
                        await firstValueFrom(this.vueClientPhilipsApi.addImages([accessionNumber]));
                    }
                } else {
                    await firstValueFrom(this.vueClientPhilipsApi.showStudyWithPid(params));
                }
            }

            this.logger.info('VueClientPhilips: Images opened successfully');
        } catch (error: unknown) {
            this.logger.error(
                'VueClientPhilips: Error opening images',
                error instanceof Error ? error.message : String(error),
            );
            throw error;
        }
    }

    /**
     * Discard currently loaded images
     */
    async discardImages(askConfirmation?: boolean): Promise<void> {
        if (!this.vueClientPhilipsApi.isConnected()) {
            return;
        }

        try {
            if (askConfirmation) {
                // TODO: Implement confirmation dialog
                const confirmed = confirm('Are you sure you want to discard the current images?');
                if (!confirmed) {
                    return;
                }
            }

            await firstValueFrom(this.vueClientPhilipsApi.closeSession());
            this.logger.info('VueClientPhilips: Images discarded');
        } catch (error: unknown) {
            this.logger.error(
                'VueClientPhilips: Error discarding images',
                error instanceof Error ? error.message : String(error),
            );
            throw error;
        }
    }

    /**
     * Set examination status (not supported by VueClientPhilips)
     */
    async setExaminationStatus?(
        idExaminations: string[],
        status: ExaminationStatusForImaging,
        studyInstanceUIDs: string[],
    ): Promise<void> {
        this.logger.warn('VueClientPhilips: setExaminationStatus not supported');
    }

    /**
     * Delete key image (not supported by VueClientPhilips)
     */
    async deleteKeyImage?(data: ModelKeyImage): Promise<void> {
        this.logger.warn('VueClientPhilips: deleteKeyImage not supported');
    }

    /**
     * Select stress echo stage (not supported by VueClientPhilips)
     */
    async usStressEchoSelectStage?(stage: string): Promise<void> {
        this.logger.warn('VueClientPhilips: usStressEchoSelectStage not supported');
    }

    /**
     * Select stress echo view (not supported by VueClientPhilips)
     */
    async usStressEchoSelectView?(view: string): Promise<void> {
        this.logger.warn('VueClientPhilips: usStressEchoSelectView not supported');
    }

    /**
     * Associate stress echo images (not supported by VueClientPhilips)
     */
    async uSStressEchoAssociateImages?(model: ModelUSStressEchoAssociateImages): Promise<void> {
        this.logger.warn('VueClientPhilips: uSStressEchoAssociateImages not supported');
    }

    /**
     * Show specific image (not supported by VueClientPhilips)
     */
    async showImage?(model: ModelShowImage): Promise<void> {
        this.logger.warn('VueClientPhilips: showImage not supported');
    }

    /**
     * Show US measure (not supported by VueClientPhilips)
     */
    async usMeasureShow?(model: ModelUSMeasureShow): Promise<void> {
        this.logger.warn('VueClientPhilips: usMeasureShow not supported');
    }

    /**
     * Open images required (compatibility method)
     */
    openImagesRequired(imagingData: any[], options?: any): void {
        this.openImages(imagingData, options || {}).catch((error: unknown) => {
            this.logger.error(
                'VueClientPhilips: Error in openImagesRequired',
                error instanceof Error ? error.message : String(error),
            );
        });
    }

    /**
     * Discard images required (compatibility method)
     */
    discardImagesRequired(): void {
        this.discardImages(false).catch((error: unknown) => {
            this.logger.error(
                'VueClientPhilips: Error in discardImagesRequired',
                error instanceof Error ? error.message : String(error),
            );
        });
    }

    /**
     * Close viewer required (compatibility method)
     */
    closeViewerRequired(): void {
        this.closeViewer().catch((error: unknown) => {
            this.logger.error(
                'VueClientPhilips: Error in closeViewerRequired',
                error instanceof Error ? error.message : String(error),
            );
        });
    }

    /**
     * Check if form is submitting (not applicable for VueClientPhilips)
     */
    isFormSubmitting(): boolean {
        return false;
    }

    /**
     * Login to VueClientPhilips viewer
     */
    async login(): Promise<void> {
        try {
            const response = await firstValueFrom(this.vueClientPhilipsApi.login());
            if (!response.success) {
                throw new Error(response.message || 'Login failed');
            }
            this.logger.info('VueClientPhilips: Login successful');
        } catch (error: unknown) {
            this.logger.error('VueClientPhilips: Login failed', error instanceof Error ? error.message : String(error));
            throw error;
        }
    }

    /**
     * Logout from VueClientPhilips viewer
     */
    async logout(): Promise<void> {
        try {
            await firstValueFrom(this.vueClientPhilipsApi.logout());
            this.logger.info('VueClientPhilips: Logout successful');
        } catch (error: unknown) {
            this.logger.error(
                'VueClientPhilips: Logout failed',
                error instanceof Error ? error.message : String(error),
            );
            throw error;
        }
    }

    /**
     * Show study in VueClientPhilips viewer
     */
    async showStudy(params: ShowStudyParams): Promise<void> {
        try {
            const response = await firstValueFrom(this.vueClientPhilipsApi.showStudy(params));
            if (!response.success) {
                throw new Error(response.message || 'Show study failed');
            }
            this.logger.info('VueClientPhilips: Study shown successfully');
        } catch (error: unknown) {
            this.logger.error(
                'VueClientPhilips: Error showing study',
                error instanceof Error ? error.message : String(error),
            );
            throw error;
        }
    }

    /**
     * Show study with PID in VueClientPhilips viewer
     */
    async showStudyWithPid(params: ShowStudyWithPidParams): Promise<void> {
        try {
            const response = await firstValueFrom(this.vueClientPhilipsApi.showStudyWithPid(params));
            if (!response.success) {
                throw new Error(response.message || 'Show study with PID failed');
            }
            this.logger.info('VueClientPhilips: Study with PID shown successfully');
        } catch (error: unknown) {
            this.logger.error(
                'VueClientPhilips: Error showing study with PID',
                error instanceof Error ? error.message : String(error),
            );
            throw error;
        }
    }

    /**
     * Show study with last presentation in VueClientPhilips viewer
     */
    async showStudyWithLastPresentation(params: ShowStudyWithLastPresentationParams): Promise<void> {
        try {
            const response = await firstValueFrom(this.vueClientPhilipsApi.showStudyWithLastPresentation(params));
            if (!response.success) {
                throw new Error(response.message || 'Show study with last presentation failed');
            }
            this.logger.info('VueClientPhilips: Study with last presentation shown successfully');
        } catch (error: unknown) {
            this.logger.error(
                'VueClientPhilips: Error showing study with last presentation',
                error instanceof Error ? error.message : String(error),
            );
            throw error;
        }
    }

    /**
     * Add images to current session
     */
    async addImages(imagePaths: string[]): Promise<void> {
        try {
            const response = await firstValueFrom(this.vueClientPhilipsApi.addImages(imagePaths));
            if (!response.success) {
                throw new Error(response.message || 'Add images failed');
            }
            this.logger.info('VueClientPhilips: Images added successfully');
        } catch (error: unknown) {
            this.logger.error(
                'VueClientPhilips: Error adding images',
                error instanceof Error ? error.message : String(error),
            );
            throw error;
        }
    }
}
